import { DocumentTypeCode } from '@quromedical/fhir-common';
import {
  DocumentClinicalReferralSchema,
  DocumentCreateMedicationScriptSchema,
  DocumentHomeBasedCareReferralSchema,
  DocumentMotivationMedicalDeviceSchema,
} from '@quromedical/models';
import { CrudForm, type SubmissionResult } from 'components/form';
import { createDocumentFields, type DocumentForm } from 'core/document';
import { Skeleton, SkeletonProvider } from 'design-system';
import type React from 'react';
import { useCallback } from 'react';
import { strings } from 'strings';
import { z } from 'zod';

interface FormProps {
  title?: string;
  onAdd: () => void;
  onChange: (data: Partial<DocumentForm>, hasErrors?: boolean) => void;
  onRemove: () => void;
  canAdd: boolean;
  canRemove: boolean;
  showErrors: boolean;
  handleSubmit: (data: DocumentForm) => Promise<SubmissionResult<unknown>>;
  documentType: DocumentTypeCode | undefined;
  loading?: boolean;
  isDuplicated?: boolean;
}

const schemaMap = {
  [DocumentTypeCode.motivationMedicalDevice]:
    DocumentMotivationMedicalDeviceSchema.extend({
      type: z
        .enum([DocumentTypeCode.motivationMedicalDevice])
        .default(DocumentTypeCode.motivationMedicalDevice),
    }),
  [DocumentTypeCode.clinicalReferral]: DocumentClinicalReferralSchema.extend({
    type: z
      .literal(DocumentTypeCode.clinicalReferral)
      .default(DocumentTypeCode.clinicalReferral),
  }),
  [DocumentTypeCode.homeBasedCareReferral]:
    DocumentHomeBasedCareReferralSchema.extend({
      type: z
        .literal(DocumentTypeCode.homeBasedCareReferral)
        .default(DocumentTypeCode.homeBasedCareReferral),
    }),
  [DocumentTypeCode.createScript]: DocumentCreateMedicationScriptSchema.extend({
    type: z
      .enum([DocumentTypeCode.createScript])
      .default(DocumentTypeCode.createScript),
  }),
} as const;

export const schema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(undefined),
  }),
  ...Object.values(schemaMap),
]);

export const CreateDocumentFormCard: React.FC<FormProps> = ({
  handleSubmit,
  onChange,
  documentType,
  onAdd,
  onRemove,
  canAdd,
  canRemove,
  showErrors,
  loading = false,
  isDuplicated = false,
}) => {
  const fields = createDocumentFields(documentType);

  const handleChange = useCallback(
    (data?: DocumentForm) => {
      const parseResult = schema.safeParse(data);
      const isValid = parseResult.success;

      onChange(
        {
          ...data,
        },
        !isValid,
      );
    },
    [onChange],
  );

  const isMultiForm = documentType === DocumentTypeCode.createScript;
  const onSubmit = async (data: DocumentForm) => {
    if (documentType === DocumentTypeCode.createScript) {
      onAdd();
      return {};
    }
    return handleSubmit(data);
  };

  return (
    <SkeletonProvider loading={loading}>
      <Skeleton>
        <CrudForm<DocumentForm>
          fields={fields}
          hasCard={false}
          initialValues={{}}
          onSubmit={onSubmit}
          onChange={handleChange}
          showErrors={showErrors}
          onChangeDebounceTime={0}
          validationSchema={documentType ? schemaMap[documentType] : undefined}
          showSubmitButton={canAdd}
          validateOnMount={showErrors}
          onSecondarySubmit={onRemove}
          secondaryButtonText={strings.FormListRemoveItem}
          buttonVariant={isMultiForm ? 'outlined' : 'contained'}
          showSecondarySubmitButton={isMultiForm ? canRemove : false}
          buttonText={isMultiForm ? strings.FormListAddItem : undefined}
          onValidChange={handleChange}
          includeIcons
          error={isDuplicated ? 'item is duplicated' : undefined}
        />
      </Skeleton>
    </SkeletonProvider>
  );
};
